'use client';

import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, { Component, ErrorInfo, useEffect } from 'react';
import { isProd } from '~/constants/environment';
import { globalError, sectionError } from '~/constants/errorBoundaryEvents';
import handleChunkLoadError from '~/utilities/handleChunkLoadError';
import { generateErrorLogger } from '~/utilities/loggers';
import ErrorBoundaryFallbackComponent, {
  OwnProps as ErrorBoundaryFallbackComponentProps
} from './ErrorBoundaryFallbackComponent';

type ForceSectionErrorProps = {
  section?: string;
};

function ForceSectionError({ section }: ForceSectionErrorProps) {
  useEffect(() => {
    if (isProd) return;

    // Force a section error to occur for testing purposes
    const urlParams = new URLSearchParams(window.location.search);
    const shouldForceError = urlParams
      .getAll('forceSectionError')
      .includes(section ?? '');

    if (shouldForceError) {
      throw new Error(`Forced error for section: ${section}`);
    }
  }, [section]);

  return null;
}

type OwnProps = {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<ErrorBoundaryFallbackComponentProps>;
  onError?: (
    _error: Error,
    _errorInfo: ErrorInfo & { section: string }
  ) => void;
  section: string;
};

type OwnState = {
  error?: Error;
  info?: ErrorInfo;
};

const logError = generateErrorLogger(globalError);

class ErrorBoundary extends Component<OwnProps, OwnState> {
  static contextType = AnalyticsContext;

  declare context: React.ContextType<typeof AnalyticsContext>;

  constructor(props: OwnProps) {
    super(props);

    this.state = {
      error: undefined,
      info: undefined
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { onError = logError, section } = this.props;
    const { trackEvent } = this.context;
    const context = { ...info, section };

    trackEvent('criticalAppError', {
      error: error.message,
      stack: error.stack
    });

    if (!handleChunkLoadError(error, sectionError, context)) {
      try {
        onError(error, context);
      } catch (ignoreError) {
        logError(error, { location: 'Error boundary' });
      }
    }

    this.setState({ error, info });
  }

  render() {
    const { children, FallbackComponent = ErrorBoundaryFallbackComponent } =
      this.props;
    const { error, info } = this.state;

    return error ? (
      <FallbackComponent
        componentStack={info?.componentStack ?? ''}
        error={error}
      />
    ) : (
      children
    );
  }
}

export default ErrorBoundary;

const logSectionError = generateErrorLogger(sectionError);

type WithErrorBoundaryOptions = {
  FallbackComponent?: OwnProps['FallbackComponent'];
  onError?: OwnProps['onError'];
  section: string;
};

export const withErrorBoundary =
  <P extends Record<string, any>>(
    ChildComponent: React.ComponentType<P>,
    {
      FallbackComponent = (_: ErrorBoundaryFallbackComponentProps) => null,
      onError = logSectionError,
      section
    }: WithErrorBoundaryOptions
  ) =>
  (props: P) => (
    <ErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={onError}
      section={section}
    >
      <ForceSectionError section={section} />
      <ChildComponent {...props} />
    </ErrorBoundary>
  );
